
import React from 'react';
import { Link } from 'react-router-dom';
import { Filter, TrendingUp, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const SalesFunnel = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Understanding the Sales Funnel Training | BPO Consultants"
                description="Learn the fundamentals of B2B sales. Understand MQLs, SQLs, and how to move prospects through the AIDA funnel."
                keywords="Sales Funnel, AIDA Model, B2B Sales Basics, Sales Pipeline Stages, MQL vs SQL"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=1200" alt="Funnel Chart" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Filter size={14} className="inline mr-1" /> Module 1: The Basics
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Understanding the <br /> Sales Funnel
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Before you can sell, you must understand how customers buy.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">From Stranger to Customer</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            The sales funnel is the visual representation of the customer journey. In this module, we break down every stage using the classic AIDA model and modern B2B pipeline definitions.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "AIDA Model",
                                    desc: "Awareness, Interest, Desire, Action. How to map your outreach to the prospect's mindset."
                                },
                                {
                                    title: "Pipeline Stages",
                                    desc: "Defining MQL (Marketing Qualified Lead), SQL (Sales Qualified Lead), and SAL (Sales Accepted Lead)."
                                },
                                {
                                    title: "Conversion Rates",
                                    desc: "Understanding the math of sales. How many cold calls does it take to get one closed deal?"
                                },
                                {
                                    title: "CRM Hygiene",
                                    desc: "Why keeping your Salesforce or HubSpot data clean is critical for forecasting."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <TrendingUp size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Key Concepts</h2>
                        <div className="mt-4 space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">What is a lead vs. a prospect?</h3>
                                <p className="text-gray-600">A lead is anyone you *could* sell to. A prospect is someone who has expressed interest or fits your ICP.</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">Why is the funnel shaped like a V?</h3>
                                <p className="text-gray-600">Because you will always have more people at the top (Awareness) than at the bottom (Closed Won). It's a game of attrition.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Build Your Foundation</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default SalesFunnel;
